import React, {
  useContext, useState, useEffect, useRef, useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { useMutation, useQuery } from '@apollo/react-hooks';
import Icon from './Icon';
import { IconEnum as Icons } from './Icons';
import colors from '../styles/colors';
import AccessLevels from '../util/AccessLevels';
import { UserContext } from '../context/UserContext';
import { media, useTabletMedia, useDesktopMedia } from '../util/MediaQuery';
import { isGuidedOnboardingEnabled } from '../util/guidedOnboarding';
import Logo from '../assets/images/cto-logo-bug.png';
import BaseModal, { ModalList } from './modals/BaseModal';
import { AppContext } from '../context/AppContext';
import {
  GET_ALL_ORGANIZATIONS,
  GET_USER_ORGANIZATION,
  SWITCH_ORGANIZATION,
} from '../graphql/queries';

const SideNavContainer = styled.nav`
  overflow-y: auto;
  overflow-x: visible;
  width: 80px;
  min-width: 80px;
  height: 100%;
  background: ${colors.white};
  flex-direction: column;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  align-items: center;
  top: 0;
  left: 0;
  transition: 0.5s;
  z-index: 1400;
  position: fixed;
  ${media.desktop`
    display: flex;
  `};

  @media screen and (max-width: 991px) {
    width: 250px;
    min-width: 250px;
    top: 0;
    left: ${(props) => (props.display === '1' ? '0' : '-100%')};
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  @media screen and (max-width: 767px) {
    width: 75%;
    min-width: 75%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const SideNavInner = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: inherit;
`;
const NavItem = styled.div`
  margin-bottom: 50px;
  color: ${(props) => (props.active ? colors.seaGreen : colors.grey3)};
  cursor: pointer;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  @media screen and (max-width: 991px) {
    margin-bottom: 25px;
    position: relative;
    align-items: center;
    justify-content: flex-start;
    display: flex;
    padding: 6px 25px;
    color: ${(props) => (props.active ? colors.seaGreen : colors.grey3)};
    width: 100%;
    box-sizing: border-box;
  }
`;

const MenuText = styled.span`
  position: absolute;
  left: ${(props) => (props.bottom ? '70%' : '21%')};
  padding: 0 0 0 14px;
  font-size: 14px;
  display: none;

  @media screen and (max-width: 991px) {
    display: block;
    position: static;
    padding: 0 0 0 14px;
    left: auto;
  }
`;

const LogoImg = styled.img`
  width: 50px;
`;

const LogoSquare = styled.div`
  width: 100%;
  height: 80px;
  background: ${colors.grey6};
  margin-bottom: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BottomButtons = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  display: block;
  width: 80px;
  justify-content: center;
  background-color: ${colors.white};
  @media screen and (max-width: 991px) {
    justify-content: flex-start;
    position: relative;
    padding-left: unset;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }
`;

const CreateOrgButton = styled.div`
  font-size: 14px;
  padding: 12px;
  cursor: pointer;
  color: #27bbad;
  font-weight: bold;
  text-align: center;
  background: rgba(26, 188, 156, 0.1);
  transition: background 0.2s, color 0.2s;
  &:hover {
    background-color: #27bbad;
    color: #fff;
  }
`;

const OrgListItem = styled.div`
  color: ${(props) => (props.active ? '#fff' : '#27bbad')};
  font-size: 14px;
  padding: 12px;
  border-radius: 6px;
  background-color: ${(props) => (props.active ? '#27bbad' : 'transparent')};
  align-items: center;
  display: flex;
  justify-content: center;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  transition: background-color 0.2s, color 0.2s;
  margin-bottom: 8px;
  white-space: normal;
  overflow-wrap: break-word;
  &:hover {
    background-color: ${(props) => (props.active ? '#219a92' : '#e0f7f5')};
    color: ${(props) => (props.active ? '#fff' : '#219a92')};
  }
`;

const OrgModalCloseButton = styled.button`
  position: absolute;
  right: 10px;
  top: 10px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  transition: color 0.2s, background 0.2s;
  &:hover {
    color: #fff;
    background: #27bbad;
    border-radius: 30%;
  }
`;
const OrgModalHeader = styled.div`
  padding: 12px 12px 10px 10px;
  margin-bottom: 12px;
  position: relative;
  flex-shrink: 0;
  background: rgba(26, 188, 156, 0.07);
  border-radius: 6px 6px 0 0;
`;
const OrgModalTitle = styled.h3`
  font-size: 16px;
  padding: 12px;
  text-align: center;
  color: #27bbad;
  margin: 0;
`;

const OrgModalFooter = styled.div`
  padding: 6px 4px;
  border-top: 1px solid #eee;

  border-radius: 0 0 6px 6px;
  display: flex;
  justify-content: center;
`;

const OrgModalContent = styled.div`
  overflow-y: auto;
  padding: 0 15px 15px 15px;
  flex-grow: 1;
`;

const OrgListContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 25px 15px 50px;
  box-sizing: border-box;
  margin-bottom: 25px;
  @media screen and (min-width: 992px) {
    display: none;
  }
`;

const OrgCreateButton = styled.div`
  font-size: 14px;
  padding: 10px 12px;
  cursor: pointer;
  color: #27bbad;
  font-weight: bold;
  text-align: center;
  background: rgba(26, 188, 156, 0.1);
  border-radius: 6px;
  transition: background 0.2s, color 0.2s;
  margin-top: 8px;
  width: 100%;
  box-sizing: border-box;
  &:hover {
    background-color: #27bbad;
    color: #fff;
  }
`;

const SuperAdminNavWrapper = styled.div`
  position: relative;
`;

const SuperAdminHoverMenu = styled.div`
  position: fixed;
  min-width: 170px;
  background: ${colors.white};
  border: 1px solid ${colors.grey5};
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  z-index: 2000;
  padding: 6px 0;

  @media screen and (max-width: 991px) {
    position: static;
    margin: 0 20px 20px 25px;
    min-width: auto;
    box-shadow: none;
  }
`;

const SuperAdminMenuItem = styled.div`
  font-size: 13px;
  color: ${(props) => (props.active ? colors.seaGreen : colors.grey3)};
  padding: 8px 12px;
  cursor: pointer;
  &:hover {
    background: ${colors.grey6};
    color: ${colors.seaGreen};
  }
`;

function GetSideNavButtons({ push }, app, closeNavBar) {
  const { campaignId } = useParams();
  const { phoneNumber } = useParams();
  const navigateCloseNav = (path) => {
    closeNavBar();
    push(path);
  };

  return [
    {
      icon: Icons.HomeRegular,
      onClick: () => navigateCloseNav('/dashboard'),
      active: ['/admin/dashboard'],
      text: 'Dashboard',
      accessLevels: [AccessLevels.SuperAdmin, AccessLevels.Admin],
    },
    {
      icon: Icons.UsersRegular,
      onClick: () => navigateCloseNav('/customers'),
      active: ['/admin/customers', `/admin/customers/${phoneNumber}`],
      text: 'Contacts',
      accessLevels: [AccessLevels.SuperAdmin, AccessLevels.Admin],
      dataHelp: 'side-nav-contacts',
    },
    {
      icon: Icons.TagRegular,
      onClick: () => navigateCloseNav('/redeemCoupon'),
      active: ['/admin/redeemCoupon'],
      text: 'Redeem Discount',
      accessLevels: [
        AccessLevels.SuperAdmin,
        AccessLevels.Admin,
        AccessLevels.Server,
      ],
    },
    ...(app?.campaignPhoneNumber
      ? [
        {
          icon: Icons.SmsRegular,
          onClick: () => navigateCloseNav('/campaigns'),
          active: [
            '/admin/campaigns',
            '/admin/campaigns/new',
              `/admin/campaigns/id=${campaignId}`,
          ],
          text: 'SMS Campaigns',
          accessLevels: [AccessLevels.SuperAdmin, AccessLevels.Admin],
          dataHelp: 'side-nav-sms-campaigns',
        },
      ]
      : []),
    {
      icon: Icons.CogsRegular,
      onClick: () => navigateCloseNav('/settings'),
      active: ['/admin/settings'],
      text: 'Settings',
      accessLevels: [AccessLevels.SuperAdmin, AccessLevels.Admin],
      dataHelp: 'side-nav-settings',
    },

    {
      icon: Icons.SuperAdmin,
      onClick: () => navigateCloseNav('/superAdmin'),
      active: ['/admin/superAdmin'],
      text: 'Superadmin',
      accessLevels: [
        AccessLevels.SuperAdmin,
        // AccessLevels.Admin,
      ],
    },

  ];
}

function getBottomButtons(
  setLogoutModalIsVisible,
  closeNavBar,
  setShowOrgSwitcher,
  compassOptions,
) {
  const buttons = [
    {
      icon: Icons.TableCells,
      onClick: () => setShowOrgSwitcher(true),
      active: ['/admin', 'org-switcher'],
      text: 'Organization Switcher',
      accessLevels: [AccessLevels.Admin, AccessLevels.Server],
    },
  ];

  if (compassOptions?.showCompass) {
    buttons.push({
      icon: Icons.CompassRegular,
      useCompassActive: true,
      onClick: () => {
        closeNavBar();
        compassOptions.onCompassClick();
      },
      active: [],
      text: 'Account setup',
      dataHelp: 'onboarding-compass',
      accessLevels: [
        AccessLevels.SuperAdmin,
        AccessLevels.Admin,
      ],
    });
  }

  buttons.push({
    icon: Icons.SignOut,
    onClick: () => {
      closeNavBar();
      setLogoutModalIsVisible(true);
    },
    active: [],
    text: 'Logout',
    accessLevels: [
      AccessLevels.SuperAdmin,
      AccessLevels.Admin,
      AccessLevels.Server,
    ],
  });

  return buttons;
}

function useOnClickOutside(ref, handler, ignoreRef = null) {
  useEffect(
    () => {
      const listener = (event) => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }
        // Ignore clicks inside optional portal/overlay element
        if (ignoreRef?.current && ignoreRef.current.contains(event.target)) {
          return;
        }
        handler(event);
      };
      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);
      return () => {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler, ignoreRef],
  );
}

function SideNavigation() {
  const ref = useRef();
  const { app, setApp } = useContext(AppContext);
  const history = useHistory();
  const [logoutModalIsVisible, setLogoutModalIsVisible] = useState(false);
  const { user, setUser } = useContext(UserContext);
  const accessLevel = user.currentOrganizationRole;
  const [organizations, setOrganizations] = useState([]);
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
  const [showSuperAdminMenu, setShowSuperAdminMenu] = useState(false);
  const isMobile = useTabletMedia();
  const orgSwitcherRef = useRef(null);
  const superAdminNavRef = useRef(null);
  const superAdminMenuRef = useRef(null);
  const superAdminMenuCloseTimeoutRef = useRef(null);
  const orgModalRef = useRef();
  const currentUserId = user?._id;
  const redirectToRedeemCoupon = useRef();

  const isDesktopViewport = useDesktopMedia();
  const checklistProgress = user?.organization?.onboardingChecklistProgress;
  const showCompass = isGuidedOnboardingEnabled(user)
    && isDesktopViewport
    && (accessLevel === AccessLevels.Admin || accessLevel === AccessLevels.SuperAdmin)
    && checklistProgress != null;

  const handleCompassClick = useCallback(() => {
    setApp((prev) => {
      const nextOpen = !prev.onboarding?.checklistSidebarOpen;
      return {
        ...prev,
        onboarding: {
          ...prev.onboarding,
          checklistSidebarOpen: nextOpen,
          ...(nextOpen ? { checklistDeferTourUntilStepClick: false } : {}),
        },
      };
    });
  }, [setApp]);

  const { data } = useQuery(GET_USER_ORGANIZATION, {
    skip: !currentUserId,
  });

  useEffect(() => {
    if (data?.getUserOrganization && currentUserId) {
      const roles = data.getUserOrganization;
      const myRoles = roles.filter((role) => role.userId === currentUserId);
      const formattedOrgs = myRoles.map((role) => ({
        id: role.organization._id,
        name: role.organization.name,
        role: role.accessLevel,
      }));

      setOrganizations(formattedOrgs);
    }
  }, [data, currentUserId]);

  const closeNavBar = () => {
    setApp({ ...app, showSideNav: false });
    setShowSuperAdminMenu(false);
  };

  useOnClickOutside(ref, () => {
    closeNavBar(false);
  }, superAdminMenuRef);

  useEffect(() => {
    setApp((app) => ({ ...app, showSideNav: false }));
  }, [setApp]);

  const isSuperAdmin = accessLevel === AccessLevels.SuperAdmin;
  const isAdmin = accessLevel === AccessLevels.Admin;
  const isAdminInAnyOrg = organizations.some(
    (org) => org.role === AccessLevels.Admin,
  );

  const filteredOrganizations = isSuperAdmin
    ? organizations.filter(
      (org) => org.role && org.role !== AccessLevels.SuperAdmin,
    )
    : organizations.filter((org) => org.role);

  const handleCreateNewOrganization = () => {
    history.push('/onboarding');
    setShowOrgSwitcher(false);
  };

  const { data: orgData } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: { start: 0, end: 0 },
    skip: !isSuperAdmin,
  });
  const getAllOrgData = orgData?.getAllOrganizations;

  const [switchOrganization] = useMutation(
    SWITCH_ORGANIZATION,
    {
      onCompleted(data) {
        const updatedUser = data?.switchOrganization?.user;
        const token = data?.switchOrganization?.token;
        if (!updatedUser) {
          console.error('No user returned from mutation');
          return;
        }
        localStorage.setItem('CHECK_THIS_OUT_AUTH_TOKEN', token);

        setApp((prev) => ({
          ...prev,
          onboarding: {
            ...prev.onboarding,
            checklistSidebarOpen: false,
            checklistDeferTourUntilStepClick: false,
            focusedChecklistStepId: null,
            tourScreenIndex: 0,
            continueWidgetAfterStepId: null,
            tourTargetMissStepId: null,
            tourSuppressForStepId: null,
            tourRetryKey: 0,
          },
        }));

        setUser((prev) => ({
          ...prev,
          ...updatedUser,
        }));

        setShowOrgSwitcher(false);

        if (redirectToRedeemCoupon.current) {
          redirectToRedeemCoupon.current = false;
          window.location.href = '/admin/redeemCoupon';
        } else {
          window.location.reload();
        }
      },
      onError(error) {
        console.error('Error switching organization:', error.message);
      },
    },
  );

  const handleSwitchOrganization = (org) => {
    const orgId = org._id || org.id;
    if (user.organizationContextId === orgId) return;

    if (org.role === 'Server') {
      history.push('/redeemCoupon');
    } else {
      redirectToRedeemCoupon.current = false;
    }

    switchOrganization({
      variables: { organizationId: orgId },
    });
  };

  useEffect(() => {
    if (user?.organizations) {
      setOrganizations(user.organizations);
    }
  }, [user?.organizations]);

  useEffect(() => {
    if (!showOrgSwitcher) return;
    function handleClickOutside(event) {
      if (orgModalRef.current && !orgModalRef.current.contains(event.target)) {
        setShowOrgSwitcher(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOrgSwitcher]);

  useEffect(() => () => {
    if (superAdminMenuCloseTimeoutRef.current) {
      clearTimeout(superAdminMenuCloseTimeoutRef.current);
    }
  }, []);

  const organizationsToShow = isSuperAdmin
    ? getAllOrgData || []
    : filteredOrganizations;

  const superAdminMenuItems = [
    { text: 'Metrics/Clients', route: '/superAdmin' },
    { text: 'Campaigns', route: '/superAdminCampaigns' },
    { text: 'Customers', route: '/superAdminCustomers' },
  ];

  const openSuperAdminMenu = () => {
    if (superAdminMenuCloseTimeoutRef.current) {
      clearTimeout(superAdminMenuCloseTimeoutRef.current);
      superAdminMenuCloseTimeoutRef.current = null;
    }
    setShowSuperAdminMenu(true);
  };

  const scheduleCloseSuperAdminMenu = () => {
    if (superAdminMenuCloseTimeoutRef.current) {
      clearTimeout(superAdminMenuCloseTimeoutRef.current);
    }
    superAdminMenuCloseTimeoutRef.current = setTimeout(() => {
      setShowSuperAdminMenu(false);
    }, 120);
  };

  return (
    <>
      <BaseModal
        open={logoutModalIsVisible}
        modalType={ModalList.LogoutModal}
        onClose={setLogoutModalIsVisible}
      />
      <SideNavContainer
        display={app?.showSideNav ? '1' : ''}
        id="leftNavBar"
        ref={ref}
      >
        <SideNavInner>
          <LogoSquare>
            <LogoImg src={Logo} />
          </LogoSquare>
          {GetSideNavButtons(
            history,
            app,
            closeNavBar,
            setShowOrgSwitcher,
            orgSwitcherRef,
            showOrgSwitcher,
          ).map((b, i) => {
            const pathname = window.location.pathname;
            const isTableCells = b.icon === Icons.TableCells;
            const isSuperAdminIcon = b.icon === Icons.SuperAdmin;
            const isSuperAdminRoute = [
              '/admin/superAdmin',
              '/admin/superAdminCampaigns',
              '/admin/superAdminCustomers',
            ].includes(pathname);
            const isActive = isTableCells
              ? showOrgSwitcher
              : isSuperAdminIcon
                ? isSuperAdminRoute
                : b.active.includes(pathname);
            return (
              <React.Fragment key={i}>
                {b.accessLevels.includes(accessLevel) && (
                  isSuperAdminIcon ? (
                    <SuperAdminNavWrapper
                      key={i}
                      onMouseEnter={!isMobile ? openSuperAdminMenu : undefined}
                      onMouseLeave={!isMobile ? scheduleCloseSuperAdminMenu : undefined}
                    >
                      <NavItem
                        ref={superAdminNavRef}
                        data-tip={undefined}
                        data-help={b.dataHelp}
                        onClick={() => {
                          if (isMobile) {
                            setShowSuperAdminMenu((old) => !old);
                          } else {
                            b.onClick();
                          }
                        }}
                        active={isActive ? 1 : 0}
                      >
                        <Icon active={isActive ? 1 : 0} icon={b.icon} />
                        <MenuText>{b.text}</MenuText>
                      </NavItem>
                      {showSuperAdminMenu && isMobile && (
                        <SuperAdminHoverMenu>
                          {superAdminMenuItems.map((item) => {
                            const itemPath = `/admin${item.route}`;
                            const isItemActive = pathname === itemPath;
                            return (
                              <SuperAdminMenuItem
                                key={item.route}
                                active={isItemActive ? 1 : 0}
                                onClick={() => {
                                  setShowSuperAdminMenu(false);
                                  closeNavBar();
                                  history.push(item.route);
                                }}
                              >
                                {item.text}
                              </SuperAdminMenuItem>
                            );
                          })}
                        </SuperAdminHoverMenu>
                      )}
                    </SuperAdminNavWrapper>
                  ) : (
                    <NavItem
                      key={i}
                      data-tip={b.text}
                      data-help={b.dataHelp}
                      onClick={() => b.onClick()}
                      active={isActive ? 1 : 0}
                    >
                      <Icon active={isActive ? 1 : 0} icon={b.icon} />
                      <MenuText>{b.text}</MenuText>
                    </NavItem>
                  )
                )}
              </React.Fragment>
            );
          })}
          <BottomButtons>
            {getBottomButtons(
              setLogoutModalIsVisible,
              closeNavBar,
              setShowOrgSwitcher,
              { showCompass, onCompassClick: handleCompassClick },
            ).map((b, i) => {
              const isOrgSwitcher = b.icon === Icons.TableCells;
              return (
                <React.Fragment key={i}>
                  {b.accessLevels.includes(accessLevel) && (
                    <>
                      <NavItem
                        onClick={b.onClick}
                        data-tip={b.text}
                        data-help={b.dataHelp}
                      >
                        <Icon
                          icon={b.icon}
                          active={
                            b.useCompassActive
                              ? (app?.onboarding?.checklistSidebarOpen ? 1 : 0)
                              : isOrgSwitcher
                                ? (showOrgSwitcher ? 1 : 0)
                                : b.active.includes(window.location.pathname) ? 1 : 0
                          }
                        />
                        <MenuText bottom>{b.text}</MenuText>
                      </NavItem>
                      {isOrgSwitcher && isMobile && (
                        <OrgListContainer>
                          {organizationsToShow.length === 0 ? (
                            <div
                              style={{
                                textAlign: 'center',
                                color: '#999',
                                padding: '12px',
                                fontSize: '14px',
                              }}
                            >
                              No other organizations available.
                            </div>
                          ) : (
                            [...organizationsToShow]
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((org) => {
                                const orgId = org._id || org.id;
                                const isActiveOrg = orgId === user.organizationContextId;
                                return (
                                  <OrgListItem
                                    key={orgId}
                                    active={isActiveOrg}
                                    onClick={() => {
                                      handleSwitchOrganization({ ...org, id: orgId });
                                      closeNavBar();
                                    }}
                                  >
                                    {org.name} {isActiveOrg && '(Active)'}
                                  </OrgListItem>
                                );
                              })
                          )}
                          {(isSuperAdmin || isAdminInAnyOrg || isAdmin) && (
                            <OrgCreateButton onClick={handleCreateNewOrganization}>
                              + Create New Organization
                            </OrgCreateButton>
                          )}
                        </OrgListContainer>
                      )}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </BottomButtons>
        </SideNavInner>
        <ReactTooltip place="right" type="dark" effect="solid" />
      </SideNavContainer>
      {showSuperAdminMenu && !isMobile && superAdminNavRef.current && ReactDOM.createPortal(
        <SuperAdminHoverMenu
          ref={superAdminMenuRef}
          style={{
            top: superAdminNavRef.current.getBoundingClientRect().top - 10,
            left: superAdminNavRef.current.getBoundingClientRect().right + 10,
          }}
          onMouseEnter={openSuperAdminMenu}
          onMouseLeave={scheduleCloseSuperAdminMenu}
        >
          {superAdminMenuItems.map((item) => {
            const pathname = window.location.pathname;
            const itemPath = `/admin${item.route}`;
            const isItemActive = pathname === itemPath;
            return (
              <SuperAdminMenuItem
                key={item.route}
                active={isItemActive ? 1 : 0}
                onClick={() => {
                  setShowSuperAdminMenu(false);
                  closeNavBar();
                  history.push(item.route);
                }}
              >
                {item.text}
              </SuperAdminMenuItem>
            );
          })}
        </SuperAdminHoverMenu>,
        document.body,
      )}
      {showOrgSwitcher && !isMobile && (
        <div
          style={{
            position: 'fixed',
            top: orgSwitcherRef.current
              ? orgSwitcherRef.current.getBoundingClientRect().top
                + window.scrollY
              : '570px',
            left: orgSwitcherRef.current
              ? orgSwitcherRef.current.getBoundingClientRect().right
              : '90px',
            height: '300px',
            width: '300px',
            backgroundColor: 'rgba(0,0,0,0.1)',
            zIndex: 1000,
            pointerEvents: 'auto',
            boxShadow: 'rgba(0,0,0,0.05) 0px 0px 0px',
          }}
          onClick={() => setShowOrgSwitcher(false)}
        >
          <div
            ref={orgModalRef}
            style={{
              marginTop: '0',
              marginLeft: '0',
              backgroundColor: 'white',
              borderRadius: '5px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              width: '300px',
              zIndex: 1001,
              height: '300px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <OrgModalHeader>
              <OrgModalCloseButton onClick={() => setShowOrgSwitcher(false)}>
                ×
              </OrgModalCloseButton>
              <OrgModalTitle>My Organizations</OrgModalTitle>
            </OrgModalHeader>
            <OrgModalContent>
              {organizationsToShow.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    color: '#999',
                    padding: '12px',
                  }}
                >
                  No other organizations available.
                </div>
              ) : (
                [...organizationsToShow]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((org) => {
                    const orgId = org._id || org.id;
                    const isActiveOrg = orgId === user.organizationContextId;
                    return (
                      <OrgListItem
                        key={orgId}
                        active={isActiveOrg}
                        onClick={() => handleSwitchOrganization({ ...org, id: orgId })}
                      >
                        {org.name} {isActiveOrg && '(Active)'}
                      </OrgListItem>
                    );
                  })
              )}
            </OrgModalContent>
            {(isSuperAdmin || isAdminInAnyOrg || isAdmin) && (
              <OrgModalFooter>
                <CreateOrgButton onClick={handleCreateNewOrganization}>
                  + Create New Organization
                </CreateOrgButton>
              </OrgModalFooter>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default SideNavigation;
